import { useCallback, useMemo, useState } from "react";

import { AssetModel, BillingModel, CustomerModel } from "@doitintl/cmp-models";
import { getCollection, type ModelIdRef } from "@doitintl/models-any";
import { useCollectionData } from "@doitintl/models-hooks";
import { Box, Stack, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTime } from "luxon";

import { useApiContext } from "../../../api/context";
import AssetsChipsSelect from "../../../Components/AssetsChipsSelect";
import { CustomerPicker } from "../../../Components/CustomerPicker/CustomerPicker";
import LoadingButton from "../../../Components/LoadingButton";
import { useErrorSnackbar, useSuccessSnackbar } from "../../../Components/SharedSnackbar/SharedSnackbar.context";

type FormData = {
  customer: { id: string; name: string } | null;
  assets: ModelIdRef<AssetModel>[];
  startDate: DateTime<true> | null;
  endDate: DateTime<true> | null;
};

const maxDate = DateTime.utc().minus({ days: 1 });
const minDate = DateTime.fromObject({ year: 2019, month: 3, day: 1 });

export const ReportUpdateToolForm = ({ loadJobs }: { loadJobs: () => Promise<void> }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    customer: null,
    assets: [],
    startDate: null,
    endDate: null,
  });

  const api = useApiContext();
  const showError = useErrorSnackbar(7);
  const showSuccessSnackbar = useSuccessSnackbar();

  const [pendingJobs = []] = useCollectionData(
    formData.customer?.id
      ? getCollection(BillingModel)
          .doc("manualOperations")
          .collection("gcpTableUpdates")
          .where("customer", "==", getCollection(CustomerModel).doc(formData.customer.id))
          .where("status", "==", "pending")
      : undefined,
    { refField: "ref", idField: "id" }
  );

  const hasPendingJobs = useMemo(() => {
    if (!formData.startDate || !formData.endDate) return false;
    const startDateStr = formData.startDate.toFormat("yyyy-MM-dd");
    const endDateStr = formData.endDate.toFormat("yyyy-MM-dd");

    return pendingJobs.some((job) => job.fromDate <= endDateStr && job.toDate >= startDateStr);
  }, [pendingJobs, formData.startDate, formData.endDate]);

  const [assets] = useCollectionData(
    formData.customer?.id
      ? getCollection(AssetModel)
          .where("customer", "==", getCollection(CustomerModel).doc(formData.customer.id))
          .where("type", "==", "google-cloud")
      : undefined,
    { refField: "ref", idField: "id" }
  );

  const handleFieldChange = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      if (field === "startDate" && value) {
        const startDate = value as DateTime<true>;

        if (
          newData.endDate &&
          (newData.endDate < startDate ||
            newData.endDate.month !== startDate.month ||
            newData.endDate.year !== startDate.year)
        ) {
          newData.endDate = null;
        }
      }

      if (field === "customer") {
        newData.assets = [];
      }

      return newData;
    });
  };

  const validateEndDate = useCallback(() => {
    if (!formData.startDate || !formData.endDate) return true;

    return (
      formData.endDate >= formData.startDate &&
      formData.endDate.month === formData.startDate.month &&
      formData.endDate.year === formData.startDate.year
    );
  }, [formData.endDate, formData.startDate]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);

    try {
      if (hasPendingJobs) {
        showError(
          "A pending job currently overlaps with the time period selected. Please run after this job completes."
        );
        return;
      }

      const payload = {
        customerId: formData.customer?.id,
        billingAccounts: formData.assets.map((asset) => asset.properties.billingAccountId),
        fromDate: formData.startDate?.toFormat("yyyy-MM-dd"),
        toDate: formData.endDate?.toFormat("yyyy-MM-dd"),
      };

      await api.post("/v1/invoicing/billing-ops-gcp-table-update", payload);
      showSuccessSnackbar("Successfully updated GCP table");
      setFormData({
        customer: null,
        assets: [],
        startDate: null,
        endDate: null,
      });
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to update GCP table");
    } finally {
      setIsSubmitting(false);
      loadJobs();
    }
  }, [api, formData, hasPendingJobs, showError, showSuccessSnackbar, loadJobs]);

  const formIncomplete = useMemo(
    () =>
      !formData.customer ||
      !formData.assets.length ||
      !formData.startDate ||
      !formData.endDate ||
      !validateEndDate() ||
      isSubmitting,
    [formData.assets, formData.customer, formData.endDate, formData.startDate, isSubmitting, validateEndDate]
  );

  return (
    <Stack
      spacing={3}
      sx={{
        mt: 3,
      }}
    >
      <CustomerPicker
        disabled={isSubmitting}
        value={formData.customer?.id}
        onChange={(_event, newCustomer) => {
          handleFieldChange(
            "customer",
            newCustomer
              ? {
                  id: newCustomer.objectID,
                  name: newCustomer.primaryDomain,
                }
              : null
          );
        }}
        getOptionLabel={({ primaryDomain, hasActiveBillingProfile }) =>
          hasActiveBillingProfile ? primaryDomain : `${primaryDomain} (inactive)`
        }
        getOptionDisabled={({ hasActiveBillingProfile }) => !hasActiveBillingProfile}
        TextFieldProps={{
          label: "Customer domain",
          variant: "outlined",
          required: true,
        }}
        data-cy="customer-select-textfield"
      />
      <AssetsChipsSelect
        options={assets ?? []}
        value={formData.assets ?? []}
        setFieldValue={(_, value) => {
          handleFieldChange("assets", value);
        }}
        disabled={isSubmitting}
        data-cy="assets-select"
        TextFieldProps={{
          margin: "none",
          required: true,
        }}
      />
      <DatePicker
        renderInput={(params) => <TextField required data-cy="start-date" margin="none" {...params} fullWidth />}
        label="Start Date"
        disabled={isSubmitting}
        value={formData.startDate}
        onChange={(value) => {
          handleFieldChange("startDate", value as DateTime<true>);
        }}
        views={["year", "month", "day"]}
        openTo="month"
        minDate={minDate}
        maxDate={maxDate}
        disableMaskedInput
        inputFormat="dd/MM/yyyy"
        data-cy="start-date"
      />
      <DatePicker
        renderInput={(params) => (
          <TextField
            required
            data-cy="end-date"
            margin="none"
            {...params}
            fullWidth
            error={!validateEndDate()}
            helperText={"End date must be in the same month as the start date"}
          />
        )}
        label="End Date"
        disabled={isSubmitting || !formData.startDate}
        value={formData.endDate}
        onChange={(value) => {
          handleFieldChange("endDate", value as DateTime<true>);
        }}
        views={["year", "month", "day"]}
        openTo="month"
        minDate={formData.startDate}
        maxDate={maxDate}
        disableMaskedInput
        inputFormat="dd/MM/yyyy"
        data-cy="end-date"
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          width: "100%",
        }}
      >
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={formIncomplete}
          loading={isSubmitting}
          data-cy="submit-btn"
          mixpanelEventId="gcp-report-update-tool.submit"
        >
          Submit
        </LoadingButton>
      </Box>
    </Stack>
  );
};
